import { network } from '@root/utils'
import { handleReturn, isHttpError, PaginatedResponse, QueryFunction } from './root'

export type ExpenseClaimStatus = 'DRAFT' | 'AWAITING_APPROVAL' | 'APPROVED' | 'PAID'

export type ExpenseClaimTag = {
  id: number
  name: string | null
  color: string | null
}

export type ExpenseClaimantProfile = {
  name: string
  iban: string
  home_address: string
}

export type ExpenseClaimLineAccount = {
  id: number
  number: string
  name: string
}

export enum ExpenseClaimLineKind {
  MILEAGE = 'MILEAGE',
  DOMESTIC_PER_DIEM = 'DOMESTIC_PER_DIEM',
  FOREIGN_PER_DIEM = 'FOREIGN_PER_DIEM',
  MEAL_ALLOWANCE = 'MEAL_ALLOWANCE',
  EXPENSE = 'EXPENSE'
}

export type ExpenseClaimLine = {
  id: number
  kind: ExpenseClaimLineKind
  description: string
  basis: string
  amount: number
  vat_rate: string
  vat_amount: number
  vat_code: number
  account: ExpenseClaimLineAccount | null
  tags: ExpenseClaimTag[]
  distance_source: 'ROUTE' | 'MANUAL' | null
  route_distance_km: string | null
  expense_id: number | null
}

export type ExpenseClaimListSummary = {
  trip_count: number
  expense_count: number
  leg_count: number
  has_partial_per_diem: boolean
  has_per_diem: boolean
}

export type ExpenseClaimListItem = {
  id: number
  number: string
  status: ExpenseClaimStatus
  total_amount: number
  created_at: string
  updated_at: string
  submitted_at: string | null
  approved_at: string | null
  paid_on: string | null
  submitted_by_name: string
  list_title: string | null
  list_title_month: number | null
  list_summary: ExpenseClaimListSummary
  claimant: {
    id: number
    first_name: string
    last_name: string
  }
}

export type ExpenseClaimApprover = {
  id: number
  first_name: string
  last_name: string
  name: string
}

export type ExpenseClaimListFilter = 'all' | 'awaiting_you' | ExpenseClaimStatus

export type ExpenseClaimListPage = PaginatedResponse<ExpenseClaimListItem> & {
  filter_counts: Partial<Record<ExpenseClaimListFilter, number>>
}

export type ExpenseClaim = ExpenseClaimListItem & {
  message_to_approver?: string
  claimant_profile?: ExpenseClaimantProfile | null
  tags?: ExpenseClaimTag[]
  status_reason?: string
  status_reason_kind?: string
  payee_name?: string
  payee_iban?: string
  booking_date?: string | null
  submitted_at?: string | null
  submitted_by_name?: string
  approved_at?: string | null
  approved_by?: ExpenseClaimApprover | null
  purchase_invoice_id?: number | null
  approved_as_accrual_based?: boolean | null
  iban_changed?: boolean
  self_approve?: boolean
  accrual_based?: boolean
  can_approve?: boolean
  can_cancel?: boolean
  lines?: ExpenseClaimLine[]
}

export type ExpenseClaimProfile = {
  name: string
  iban: string
  home_address: string
}

export type ExpenseClaimSummary = {
  awaiting_approval_count: number
}

export type ExpenseClaimListScope = 'mine' | 'approvals' | 'combined'
export type ExpenseClaimListGroup = 'drafts' | 'in_progress' | 'processed'

interface ExpenseClaimListProps {
  slug: string
}

const claimsUrl = (slug: string) => `/v1/business/${slug}/expense_claims/`

export const fetchExpenseClaims: QueryFunction<
  ExpenseClaimListProps,
  ExpenseClaimListPage
> = async ({ slug }, params = null) => {
  const { status, data } = await network.httpClient.request<ExpenseClaimListPage>({
    url: claimsUrl(slug),
    method: 'GET',
    params,
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export const fetchExpenseClaim = async (slug: string, claimId: number): Promise<ExpenseClaim> => {
  const { status, data } = await network.httpClient.request<ExpenseClaim>({
    url: `${claimsUrl(slug)}${claimId}/`,
    method: 'GET',
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export const createExpenseClaim = async (slug: string): Promise<ExpenseClaim> => {
  const { status, data } = await network.httpClient.request<ExpenseClaim>({
    url: claimsUrl(slug),
    method: 'POST',
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export const updateExpenseClaim = async (
  slug: string,
  claimId: number,
  payload: { message_to_approver?: string; tag_ids?: number[] }
): Promise<ExpenseClaim> => {
  const { status, data } = await network.httpClient.request<ExpenseClaim>({
    url: `${claimsUrl(slug)}${claimId}/`,
    method: 'PATCH',
    data: payload,
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

const postClaimAction = async (
  slug: string,
  claimId: number,
  action: 'submit' | 'approve' | 'reject' | 'cancel_approval',
  payload: Record<string, unknown>
): Promise<ExpenseClaim> => {
  const { status, data } = await network.httpClient.request<ExpenseClaim>({
    url: `${claimsUrl(slug)}${claimId}/${action}/`,
    method: 'POST',
    data: payload,
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export const submitExpenseClaim = (
  slug: string,
  claimId: number,
  bookingDate?: string
): Promise<ExpenseClaim> =>
  postClaimAction(slug, claimId, 'submit', bookingDate ? { booking_date: bookingDate } : {})

export const approveExpenseClaim = (
  slug: string,
  claimId: number,
  bookingDate?: string
): Promise<ExpenseClaim> =>
  postClaimAction(slug, claimId, 'approve', bookingDate ? { booking_date: bookingDate } : {})

export const rejectExpenseClaim = (
  slug: string,
  claimId: number,
  reason: string
): Promise<ExpenseClaim> => postClaimAction(slug, claimId, 'reject', { reason })

export const cancelExpenseClaimApproval = (
  slug: string,
  claimId: number,
  reason: string
): Promise<ExpenseClaim> => postClaimAction(slug, claimId, 'cancel_approval', { reason })

export const updateExpenseClaimLine = async (
  slug: string,
  claimId: number,
  lineId: number,
  payload: { account_id?: number | null; tag_ids?: number[] }
): Promise<ExpenseClaim> => {
  const { status, data } = await network.httpClient.request<ExpenseClaim>({
    url: `${claimsUrl(slug)}${claimId}/lines/${lineId}/`,
    method: 'PATCH',
    data: payload,
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export const deleteExpenseClaim = async (slug: string, claimId: number): Promise<void> => {
  const { status, data } = await network.httpClient.request({
    url: `${claimsUrl(slug)}${claimId}/`,
    method: 'DELETE',
    validateStatus: () => true
  })
  handleReturn({ status, data })
}

export const recalculateExpenseClaim = async (
  slug: string,
  claimId: number
): Promise<ExpenseClaim> => {
  const { status, data } = await network.httpClient.request<ExpenseClaim>({
    url: `${claimsUrl(slug)}${claimId}/recalculate/`,
    method: 'POST',
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export const fetchExpenseClaimProfile = async (slug: string): Promise<ExpenseClaimProfile> => {
  const { status, data } = await network.httpClient.request<ExpenseClaimProfile>({
    url: `${claimsUrl(slug)}profile/`,
    method: 'GET',
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export const saveExpenseClaimProfile = async (
  slug: string,
  profile: ExpenseClaimProfile
): Promise<ExpenseClaimProfile> => {
  const { status, data } = await network.httpClient.request<ExpenseClaimProfile>({
    url: `${claimsUrl(slug)}profile/`,
    method: 'PUT',
    data: profile,
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export type IncomesRegisterRow = {
  claimant_id: number
  name: string
  mileage: number
  domestic_per_diem: number
  foreign_per_diem: number
  meal_allowance: number
  total: number
}

export type IncomesRegister = {
  month: string
  claim_count: number
  rows: IncomesRegisterRow[]
}

export const fetchIncomesRegister = async (
  slug: string,
  month: string
): Promise<IncomesRegister> => {
  const { status, data } = await network.httpClient.request<IncomesRegister>({
    url: `${claimsUrl(slug)}incomes_register/`,
    method: 'GET',
    params: { month },
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export const downloadIncomesRegisterCsv = async (slug: string, month: string): Promise<void> => {
  const response = await network.httpClient.request<Blob>({
    url: `${claimsUrl(slug)}incomes_register/`,
    method: 'GET',
    params: { month, format: 'csv' },
    responseType: 'blob',
    validateStatus: () => true
  })
  if (response.status < 200 || response.status >= 300) {
    throw new Error('download failed')
  }
  const header = String(response.headers['content-disposition'] ?? '')
  const match = /filename="([^"]+)"/.exec(header)
  const filename = match?.[1] ?? `incomes-register-${month}.csv`
  const url = URL.createObjectURL(response.data)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

export const fetchExpenseClaimSummary = async (slug: string): Promise<ExpenseClaimSummary> => {
  const { status, data } = await network.httpClient.request<ExpenseClaimSummary>({
    url: `${claimsUrl(slug)}summary/`,
    method: 'GET',
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export const expenseClaimFieldError = (error: unknown, field: string): string | null => {
  if (!isHttpError(error)) return null
  const data = error.data as {
    detail?: string[]
    field_errors?: Record<string, string[]>
    [key: string]: unknown
  }
  const fromFieldErrors = data?.field_errors?.[field]
  if (Array.isArray(fromFieldErrors) && typeof fromFieldErrors[0] === 'string') {
    return fromFieldErrors[0]
  }
  const direct = data?.[field]
  if (Array.isArray(direct) && typeof direct[0] === 'string') return direct[0]
  if (field === 'detail' && Array.isArray(data?.detail) && typeof data.detail[0] === 'string') {
    return data.detail[0]
  }
  return null
}
